import { fetchVodPlayInfo } from '@/api/common.js';

const parseOssError = (xml) => {
  const text = String(xml || '');
  const code = (text.match(/<Code>([^<]+)<\/Code>/) || [])[1];
  const message = (text.match(/<Message>([^<]+)<\/Message>/) || [])[1];
  if (code && message) {
    return `${code}: ${message}`;
  }
  return text || 'OSS upload failed';
};

export const putFileToVodOss = async (auth, file, onProgress) => {
  const url = String(auth.uploadUrl || '');
  if (!url) {
    throw new Error('Missing upload URL');
  }
  const contentType = auth.contentType || 'application/octet-stream';
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (onProgress && event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(parseOssError(xhr.responseText)));
    };
    xhr.onerror = () => reject(new Error('Network error while uploading video'));
    xhr.send(new Blob([file], { type: contentType }));
  });
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || ''));

export const toHttpsUrl = (value) => {
  const text = String(value || '');
  return text.startsWith('http://') ? `https://${text.slice(7)}` : text;
};

export const isVodId = (value) => {
  const text = String(value || '').trim();
  return !!text && !isHttpUrl(text) && !text.includes('/');
};

const VIDEO_EXT_RE = /\.(mp4|mov|avi|mkv|webm|flv|wmv|m4v)(\?|$)/i;

// 把封面 URL 规范化为"第 1 秒"截帧：
// - 已是 OSS 视频截帧 URL：强制 t_1000
// - 原始视频文件 URL：追加 t_1000 截帧参数
// - dataURL / 图片 / 其它：原样返回（无法从前端控制截帧时机）
export const normalizeCoverAt1s = (url) => {
  const src = String(url || '');
  if (!src || src.startsWith('data:') || src.startsWith('blob:')) return src;
  if (!isHttpUrl(src)) return src;
  if (src.includes('x-oss-process=video/snapshot')) {
    return src.replace(/t_\d+/, 't_1000');
  }
  if (VIDEO_EXT_RE.test(src) && !src.includes('?')) {
    return src + '?x-oss-process=video/snapshot,t_1000,f_jpg,w_500,ar_auto';
  }
  return src;
};

export const isNativePlayable = (url) => {
  const text = String(url || '');
  if (text.startsWith('blob:') || text.startsWith('data:')) {
    return true;
  }
  if (!isHttpUrl(text)) {
    return false;
  }
  const lower = text.toLowerCase();
  return !lower.includes('.m3u8') && !lower.includes('encrypt-stream') && !lower.includes('hls/sign');
};

export const vodVideoId = (item) => {
  if (!item) return '';
  if (typeof item === 'string') {
    return isVodId(item) ? item.trim() : '';
  }
  if (isVodId(item.videoUrl)) return String(item.videoUrl).trim();
  if (isVodId(item.videoId)) return String(item.videoId).trim();
  return '';
};

export const playSrc = (item) => {
  if (!item) return '';
  if (typeof item === 'string') {
    return isNativePlayable(item) ? item : '';
  }
  const candidates = [item.playUrl, item._previewUrl, item.videoUrl];
  for (const candidate of candidates) {
    if (isNativePlayable(candidate)) {
      return toHttpsUrl(candidate);
    }
  }
  return '';
};

export const posterSrc = (item) => {
  if (!item) return '';
  if (typeof item === 'object') {
    if (item.posterUrl) return normalizeCoverAt1s(toHttpsUrl(item.posterUrl));
    if (item.coverUrl) return normalizeCoverAt1s(toHttpsUrl(item.coverUrl));
  }
  const src = playSrc(item);
  if (src && isHttpUrl(src) && !src.includes('.m3u8') && !src.startsWith('blob:') && !src.includes('?')) {
    return normalizeCoverAt1s(src);
  }
  return '';
};

export const waitForVodPlayInfo = async (videoId, tries = 8, interval = 1500) => {
  if (!isVodId(videoId)) return null;
  for (let i = 0; i < tries; i++) {
    try {
      const res = await fetchVodPlayInfo(videoId);
      if (res.code === 0 && res.data && res.data.playUrl) {
        return res.data;
      }
    } catch (e) {
      // transcoding may still be running
    }
    if (i < tries - 1) {
      await sleep(interval);
    }
  }
  return null;
};

export const hydrateVodItems = async (list) => {
  if (!list || !list.length) return list;
  await Promise.all(
    list.map(async (item) => {
      if (!item || !isVodId(item.videoUrl)) return;
      if (item.playUrl) item.playUrl = toHttpsUrl(item.playUrl);
      if (item.coverUrl) item.coverUrl = normalizeCoverAt1s(toHttpsUrl(item.coverUrl));
      if (isNativePlayable(item.playUrl) && item.coverUrl) return;
      const info = await waitForVodPlayInfo(item.videoUrl, 6, 1500);
      if (!info) return;
      item.playUrl = toHttpsUrl(info.playUrl);
      // 优先用播放地址生成第 1 秒截帧作为封面，避免后端返回第 0 帧封面
      const playForCover = item.playUrl;
      if (isNativePlayable(playForCover) && isHttpUrl(playForCover) && VIDEO_EXT_RE.test(playForCover) && !playForCover.includes('?')) {
        item.coverUrl = playForCover + '?x-oss-process=video/snapshot,t_1000,f_jpg,w_500,ar_auto';
      } else if (info.coverUrl) {
        item.coverUrl = normalizeCoverAt1s(toHttpsUrl(info.coverUrl));
      }
    })
  );
  return list;
};
